import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Grid, Typography, Card, CircularProgress, Divider, Chip, Breadcrumbs, Avatar, Paper } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useParams, Link } from 'react-router-dom';
 
import { getPermissionDetailStore } from 'store/permissions/thunks';
import { IconByString } from 'components/IconByString';

import VpnKeyIcon from '@mui/icons-material/VpnKey';
import AppsIcon from '@mui/icons-material/Apps';
import DescriptionIcon from '@mui/icons-material/Description';

const StyledCard = styled(Card)(({ theme }) => ({
  marginTop: 30,
  padding: 32,
  borderRadius: 16,
  boxShadow: '0px 12px 24px -4px rgba(145, 158, 171, 0.12), 0px 0px 2px 0px rgba(145, 158, 171, 0.2)',
  border: '1px solid rgba(145, 158, 171, 0.16)'
}));

const InfoPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: 16,
  border: '1px solid rgba(145, 158, 171, 0.12)',
  boxShadow: 'none',
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(2.5),
  transition: 'box-shadow 0.3s, transform 0.3s',
  '&:hover': {
    boxShadow: '0px 8px 24px rgba(145, 158, 171, 0.16)',
    transform: 'translateY(-2px)'
  }
}));

export const DetailPermissions = () => {
  const { id } = useParams();
  const dispatch = useDispatch<any>();

  const { permission_detail } = useSelector((state: any) => state.permissions);

  useEffect(() => {
    if (id) {
      dispatch(getPermissionDetailStore(id));
    }
  }, [dispatch, id]);


  if (!permission_detail || permission_detail.id !== id) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }


  return (
    <Box sx={{ width: '100%' }}>
      <Typography fontSize={25} color="textPrimary" sx={{ fontWeight: 700 }}>
        Detalle del Permiso
      </Typography>

      <Breadcrumbs aria-label="breadcrumb" sx={{ padding: 2 }}>
          <Typography color="textPrimary" sx={{ fontWeight: 700 }}>Manager</Typography>
          <Link  to="/manager/permisos">
            Listar Permisos
          </Link>
          <Typography color="textPrimary">Detalle del Permiso</Typography>
      </Breadcrumbs>
      
      <Divider sx={{ marginBottom: 3 }}/>
      
      <StyledCard>
        {/* Header Section */}
        <Box display="flex" alignItems="center" gap={3} mb={4}>
          <Avatar 
            sx={{ 
                color: 'primary.dark', 
                width: 72, 
                height: 72 
            }}
          >
             <IconByString iconName={permission_detail.icon} sx={{ fontSize: 36 }} />
          </Avatar>
          <Box>
            <Typography variant="h4" fontWeight={700} gutterBottom>
              {permission_detail.name}
            </Typography>
            <Box display="flex" alignItems="center" gap={1.5}>
                <Chip 
                    label={permission_detail.status === 1 ? 'Activo' : 'Inactivo'} 
                    color={permission_detail.status === 1 ? 'success' : 'default'} 
                    size="small"
                    sx={{ fontWeight: 'bold' }} 
                />
                <Typography variant="body2" color="textSecondary">
                    Ícono asignado: <strong>{permission_detail.icon}</strong>
                </Typography>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ mb: 4 }} />

        <Grid container spacing={3}>
            <Grid size={{xs: 12, md: 6}}>
                <InfoPaper>
                    <Avatar sx={{ bgcolor: 'rgba(0, 167, 111, 0.1)', color: '#00A76F' }}>
                        <VpnKeyIcon />
                    </Avatar>
                    <Box>
                        <Typography variant="overline" color="textSecondary" sx={{ mb: 0.5, display: 'block' }}>Slug del Permiso</Typography>
                        <Typography variant="subtitle1" fontWeight={600}>{permission_detail.slug}</Typography>
                    </Box>
                </InfoPaper>
            </Grid>

            <Grid size={{xs: 12, md: 6}}>
                <InfoPaper>
                    <Avatar sx={{ bgcolor: 'rgba(255, 171, 0, 0.1)', color: '#FFAB00' }}>
                        <AppsIcon />
                    </Avatar>
                    <Box>
                        <Typography variant="overline" color="textSecondary" sx={{ mb: 0.5, display: 'block' }}>Aplicación</Typography>
                        <Typography variant="subtitle1" fontWeight={600}>{ permission_detail.application?.name || 'N/A'}</Typography>
                    </Box>
                </InfoPaper>
            </Grid>

            <Grid size={{xs: 12}}>
                <InfoPaper>
                    <Avatar sx={{ bgcolor: 'rgba(24, 119, 242, 0.1)', color: '#1877F2' }}>
                        <DescriptionIcon />
                    </Avatar>
                    <Box>
                        <Typography variant="overline" color="textSecondary" sx={{ mb: 0.5, display: 'block' }}>Descripción</Typography>
                        <Typography variant="body1">{permission_detail.description}</Typography>
                    </Box>
                </InfoPaper>
            </Grid>
            
            <Grid size={{xs: 12}}>
                <Box mt={2}>
                    <Typography variant="overline" color="textSecondary" sx={{ mb: 1, display: 'block' }}>Estructura de Hijos (JSON)</Typography>
                    <Box 
                        component="pre" 
                        sx={{ 
                            p: 2, 
                            bgcolor: '#f4f6f8', 
                            borderRadius: 2, 
                            overflowX: 'auto',
                            fontSize: '0.85rem',
                            border: '1px solid #e5e7eb'
                        }}
                    >
                        {typeof permission_detail.childrens === 'string' 
                            ? permission_detail.childrens 
                            : JSON.stringify(permission_detail.childrens, null, 2)}
                    </Box>
                </Box>
            </Grid>
        </Grid>
      </StyledCard>
    </Box>
  );
};
